import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useDeleteSnapshot, useNetworthSnapshotDetail } from "../../hooks/networth";
import { formatDateShort } from "../../lib/format";
import type { SnapshotListItem } from "../../lib/networth";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { AssetClassRow } from "./AssetClassRow";

interface Props {
  snapshots: SnapshotListItem[];
}

function formatEur(cents: number): string {
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

const CLASSES = ["LIQUIDE", "ANLAGEN", "IMMOBILIE", "SONSTIGE", "VERBINDLICHKEITEN"] as const;

export function NetworthHistoryTable({ snapshots }: Props) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const detailDate = params.get("detail");
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const deleteMutation = useDeleteSnapshot();

  function toggleDetail(stichtag: string) {
    const next = new URLSearchParams(params);
    if (detailDate === stichtag) next.delete("detail");
    else next.set("detail", stichtag);
    setParams(next, { replace: true });
  }

  async function confirmDeleteSnapshot(stichtag: string) {
    await deleteMutation.mutateAsync(stichtag);
    setConfirmDelete(null);
  }

  return (
    <>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b bg-gray-50">
            <th className="p-2">{t("networth.list.column.stichtag")}</th>
            <th className="p-2 text-right">{t("networth.list.column.total")}</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s) => (
            <ExpandableRow
              key={s.stichtag}
              snapshot={s}
              expanded={detailDate === s.stichtag}
              onToggle={() => toggleDetail(s.stichtag)}
              onDelete={() => setConfirmDelete(s.stichtag)}
            />
          ))}
        </tbody>
      </table>
      {confirmDelete && (
        <ConfirmDialog
          open
          title={t("networth.list.delete.title")}
          message={t("networth.list.delete.message", { stichtag: formatDateShort(confirmDelete) })}
          confirmLabel={t("networth.list.delete.confirm")}
          destructive
          onConfirm={() => confirmDeleteSnapshot(confirmDelete)}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </>
  );
}

interface RowProps {
  snapshot: SnapshotListItem;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

function ExpandableRow({ snapshot, expanded, onToggle, onDelete }: RowProps) {
  const { t } = useTranslation();
  const detail = useNetworthSnapshotDetail(expanded ? snapshot.stichtag : "");

  return (
    <>
      <tr className="border-b">
        <td className="p-2">
          <button type="button" className="text-blue-600 hover:underline" onClick={onToggle}>
            {expanded ? "▼ " : "▶ "}
            {formatDateShort(snapshot.stichtag)}
          </button>
        </td>
        <td className="p-2 text-right font-mono">{formatEur(snapshot.totalCents)}</td>
        <td className="p-2">
          <button type="button" className="text-red-700 text-sm" onClick={onDelete}>
            {t("networth.list.delete.label")}
          </button>
        </td>
      </tr>
      {expanded && detail.data && (
        <tr>
          <td colSpan={3} className="p-2 bg-slate-50">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="p-1">{t("networth.detail.column.class")}</th>
                  <th className="p-1 text-right">{t("networth.detail.column.value")}</th>
                  <th className="p-1">{t("networth.detail.column.source")}</th>
                  <th className="p-1">{t("networth.detail.column.note")}</th>
                </tr>
              </thead>
              <tbody>
                {CLASSES.map((c) => (
                  <AssetClassRow key={c} assetClass={c} row={detail.data.classes[c]} />
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}
